/**
 * VideoSourceCards - Video source category selector.
 * Shows Stock Videos, YouTube Clips, AI Videos (expands sub-panel), and AI Director.
 * Uses SegmentedPicker for a consistent always-visible quick-tweak UX.
 */

import { Film, Sparkles, Brain, Monitor } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { VIDEO_SOURCES, type VideoSource } from '@/constants/videoSources';
import { isServerlessMode } from '@/utils/apiMode';
import { SegmentedPicker } from '../../common/SegmentedPicker';
import type { SegmentOption } from '../../common/SegmentedPicker';
import { getVideoCategory, isAIVideoSource, type VideoCategory } from './VideoSourceCards.utils';

interface VideoSourceCardsProps {
  videoSource: VideoSource;
  onVideoSourceChange: (source: VideoSource) => void;
  disabled: boolean;
  /** Currently active category (for highlighting AI Videos when a sub-provider is selected) */
  activeCategory?: VideoCategory;
  /** Callback when a category is selected (before setting a specific source) */
  onCategoryChange?: (category: VideoCategory) => void;
}

/**
 * YouTube SVG icon — kept separate since SegmentedPicker iconNode accepts ReactNode.
 */
function YouTubeIcon() {
  return (
    <svg viewBox="0 0 28 20" className="h-5 w-7" aria-label="YouTube">
      <rect width="28" height="20" rx="6" fill="#FF0000" />
      <polygon points="11,5 11,15 20,10" fill="white" />
    </svg>
  );
}

export function VideoSourceCards({
  videoSource,
  onVideoSourceChange,
  disabled,
  activeCategory: activeCategoryProp,
  onCategoryChange,
}: VideoSourceCardsProps) {
  const { t } = useTranslation();
  const isCloud = isServerlessMode();

  const activeCategory = activeCategoryProp ?? getVideoCategory(videoSource);

  const handleChange = (categoryValue: string) => {
    const category = categoryValue as VideoCategory;

    // YouTube in cloud mode: redirect to download page, don't change source
    if (isCloud && category === 'youtube') {
      window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
      return;
    }

    onCategoryChange?.(category);

    if (category === 'stock') {
      onVideoSourceChange(VIDEO_SOURCES.STOCK);
    } else if (category === 'youtube') {
      onVideoSourceChange(VIDEO_SOURCES.YOUTUBE);
    } else if (category === 'ai_director') {
      onVideoSourceChange(VIDEO_SOURCES.AI_DIRECTOR);
    } else if (category === 'ai_video' && !isAIVideoSource(videoSource)) {
      onVideoSourceChange(VIDEO_SOURCES.AI_VEO);
    }
  };

  const options: SegmentOption[] = [
    {
      value: 'stock',
      label: t('videoSource.stock.label', { defaultValue: 'Stock Videos' }),
      description: t('videoSource.stock.description', { defaultValue: 'Pexels & Pixabay' }),
      iconNode: <Film className="h-5 w-5" />,
      trailingMeta: t('videoSource.stock.pricing', { defaultValue: 'Free' }),
    },
    {
      value: 'youtube',
      label: t('videoSource.youtube.label', { defaultValue: 'YouTube Clips' }),
      description: isCloud
        ? t('videoSource.desktopOnly', { defaultValue: 'Desktop app only' })
        : t('videoSource.youtube.description', { defaultValue: 'Curated clips' }),
      iconNode: <YouTubeIcon />,
      trailingMeta: isCloud
        ? undefined
        : t('videoSource.youtube.pricing', { defaultValue: 'Free' }),
      disabled: isCloud,
      disabledReason: t('videoSource.desktopOnly', {
        defaultValue: 'Download the desktop app for free access!',
      }),
    },
    {
      value: 'ai_video',
      label: t('videoSource.aiVideo.label', { defaultValue: 'AI Videos' }),
      description: t('videoSource.aiVideo.description', { defaultValue: '15+ AI providers' }),
      iconNode: <Sparkles className="h-5 w-5" />,
      trailingMeta: t('videoSource.aiVideo.pricing', { defaultValue: 'Varies' }),
    },
    {
      value: 'ai_director',
      label: t('videoSource.aiDirector.label', { defaultValue: 'AI Director' }),
      description: t('videoSource.aiDirector.description', { defaultValue: 'Smart auto-select' }),
      iconNode: <Brain className="h-5 w-5" />,
      trailingMeta: t('videoSource.aiDirector.pricing', { defaultValue: 'Varies' }),
    },
  ];

  // When youtube is disabled in cloud mode, show a tooltip via the SegmentedPicker
  // disabled mechanism. We still want the other 3 options fully interactive.
  const cloudYoutubeInfo = isCloud ? (
    <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-400">
      <Monitor className="h-3 w-3" />
      <span>
        {t('videoSource.desktopOnly', {
          defaultValue: 'Download the desktop app for free access!',
        })}
      </span>
    </div>
  ) : null;

  return (
    <div className="space-y-1">
      <SegmentedPicker
        options={options}
        value={activeCategory}
        onChange={handleChange}
        size="md"
        layout="equal"
        disabled={disabled}
      />
      {cloudYoutubeInfo}
    </div>
  );
}
